<template>
  <detail
    :on-manager="onManager"
    :data="dataCollection"
    :base-info="baseInfo"
    :extra-info="extraInfo"
    resource="cloudsupplier_customers" />
</template>

<script>
import * as R from 'ramda'
import i18n from '@/locales'
import {
  getStatusTableColumn,
  getEnabledTableColumn,
  getBrandTableColumn,
} from '@/utils/common/tableColumn'
import { hasPermission } from '@/utils/auth'
import WindowsMixin from '@/mixins/windows'

export default {
  name: 'CloudAccountManagerDetail',
  mixins: [WindowsMixin],
  props: {
    data: {
      type: Object,
      required: true,
    },
    onManager: {
      type: Function,
      required: true,
    },
  },
  data () {
    return {
      cloudaccountData: {},
      extraInfo: [
        {
          title: this.$t('scope.text_531'),
          items: [
            {
              field: 'id',
              title: 'ID',
              slots: {
                default: ({ row }) => {
                  return [
                    <div class='text-truncate'>
                      <list-body-cell-wrap copy row={ row.account } message={ row.account.id } field='id' title={ row.account.id } />
                    </div>,
                  ]
                },
              },
            },
            {
              field: 'name',
              title: this.$t('scope.text_532'),
              slots: {
                default: ({ row }) => {
                  return [
                    <div class='text-truncate'>
                      <list-body-cell-wrap copy row={ row.account } field='name' message={ row.account.name } title={ row.account.name } />
                    </div>,
                  ]
                },
              },
            },
            {
              field: 'account.status',
              title: this.$t('scope.text_426'),
              slots: {
                default: ({ row }) => {
                  if (row.account.status) {
                    return [<status status={ row.account.status } statusModule='cloudaccount' />]
                  }
                  return '-'
                },
              },
            },
            {
              field: 'project_domain',
              title: i18n.t('dictionary.domain'),
              slots: {
                default: ({ row }) => {
                  const domain = row.account.project_domain || row.account.domain
                  if (!row.account.domain_id) return domain || '-'
                  if (!domain) return '-'
                  const p = hasPermission({ key: 'domains_get' })
                  let node
                  if (p) {
                    node = (
                      <list-body-cell-wrap copy row={ row.account } field='project_domain' title={ row.account.project_domain } message={domain} hideField={ true }>
                        <side-page-trigger permission='domains_get' name='DomainSidePage' id={row.account.project_domain} vm={this}>{ domain }</side-page-trigger>
                      </list-body-cell-wrap>
                    )
                  } else {
                    node = (
                      <list-body-cell-wrap copy row={ row.account } field='project_domain' title={ row.account.project_domain } message={domain} />
                    )
                  }
                  return [
                    <div class='text-truncate'>{ node }</div>,
                  ]
                },
              },
            },
            {
              field: 'tenant',
              title: i18n.t('dictionary.project'),
              formatter: ({ row }) => {
                return row.tenant || '-'
              },
              slots: {
                default: ({ row }) => {
                  if (!row.account.tenant_id) return row.account.tenant || '-'
                  const p = hasPermission({ key: 'projects_get' })
                  let node
                  if (p) {
                    node = (
                      <list-body-cell-wrap copy row={ row.account } field='tenant' title={ row.account.tenant } hideField={ true }>
                        <side-page-trigger permission='projects_get' name='ProjectSidePage' id={row.account.tenant_id} vm={this}>{ row.account.tenant }</side-page-trigger>
                      </list-body-cell-wrap>
                    )
                  } else {
                    node = (
                      <list-body-cell-wrap copy row={ row.account } field='tenant' title={ row.account.tenant } />
                    )
                  }
                  return [
                    <div class='text-truncate'>{ node }</div>,
                  ]
                },
              },
            },
            getBrandTableColumn({ field: 'account.brand' }),
            {
              field: 'share_mode',
              title: this.$t('scope.text_533'),
              width: 100,
              slots: {
                default: ({ row }) => {
                  const shareMode = {
                    account_domain: this.$t('scope.text_534'),
                    system: this.$t('scope.text_535'),
                    provider_domain: this.$t('scope.text_536'),
                  }
                  return [
                    <div class='text-truncate'>
                      { shareMode[row.account.share_mode] || '-' }
                    </div>,
                  ]
                },
              },
            },
            {
              field: 'account',
              title: this.$t('scope.text_406'),
              slots: {
                default: ({ row }) => {
                  return [
                    <div class='text-truncate'>
                      <list-body-cell-wrap copy row={ row.account } field='account' message={ row.account.account } title={ row.account.account } />
                    </div>,
                  ]
                },
              },
            },
            getEnabledTableColumn({ field: 'account.enabled' }),
          ],
        },
      ],
    }
  },
  computed: {
    dataCollection () {
      return {
        ...this.data,
        account: {
          ...this.cloudaccountData,
        },
      }
    },
    baseInfo () {
      const arr = [
        {
          field: 'provider',
          title: this.$t('scope.text_504'),
          width: 100,
          slots: {
            default: ({ row }, h) => {
              let ret = ''
              if (row.provider) {
                ret = [<icon class='mr-2' type={ row.provider.toLowerCase() } style="font-size: 20px" />]
              } else {
                ret = '-'
              }
              return ret
            },
          },
        },
        getEnabledTableColumn({ field: 'enabled_status' }),
      ]
      if (!R.isNil(this.data.connect_status)) {
        arr.push(getStatusTableColumn({ field: 'connect_status', statusModule: 'cloudaccount' }))
      }
      return arr
    },
  },
  async created () {
    if (this.data.cloudaccount_id) {
      const { data } = await this.fetchCloudAccount()
      this.cloudaccountData = data
    }
  },
  methods: {
    fetchCloudAccount () {
      return new this.$Manager('cloudaccounts').get({ id: this.data.cloudaccount_id })
    },
  },
}
</script>

<style lang="less" scoped>
  ::v-deep .imageIcon{
    font-size: 16px;
    cursor: pointer;
    color: #1890ff;
  }
</style>
